import uniq from 'lodash/uniq'
import { useRouteQuery } from '@vueuse/router'
import type { SearchFilterOptions } from '~/composables/useSearchFilters'
import cloneDeep from 'lodash/cloneDeep'

export const useParseQueryParamsToSearchFilters = (
  defaultFilters:
    | ComputedRef<SearchFilterOptions | undefined>
    | Ref<SearchFilterOptions | undefined> = ref(undefined)
) => {
  const route = useRoute()
  const filters = ref<SearchFilterOptions>({})
  const router = useRouter()
  // Route query
  const categoriesQuery = useRouteQuery('categories')
  const locationQuery = useRouteQuery('location')
  const locationTypeQuery = useRouteQuery('locationType')
  const citySlugQuery = useRouteQuery('citySlug')
  const datesQuery = useRouteQuery('dates')
  const queryParam = useRouteQuery('query')
  const priceMaxQuery = useRouteQuery('priceMax')
  const priceMinQuery = useRouteQuery('priceMin')
  const hasAvailableDatesQuery = useRouteQuery('hasAvailableDates')
  const hasPromotion = useRouteQuery('hasPromotion')
  const typeOfClass = useRouteQuery('typeOfClass')
  // const onlineQuery = useRouteQuery('online')
  // const directEvents = useRouteQuery('directEvents')
  // const publicEvents = useRouteQuery('publicEvents')
  const languagesQuery = useRouteQuery('languages')
  const resetFiltersQuery = useRouteQuery('resetFilters')
  // const pageQuery = useRouteQuery('page')

  // Route Params
  // const categoryParam = useRouteParams('category')
  // const locationParam = useRouteParams('location')

  const locationSlug = computed(() => {
    const fromQuery =
      locationQuery.value?.toString() || citySlugQuery.value?.toString()
    if (fromQuery) return fromQuery
    return defaultFilters.value?.location?.toString() ?? ''
  })

  const updateFilters = () => {
    // filters.value.categories = categSlug ? [categSlug] : filters.value.categories
    filters.value.categories = defaultFilters.value?.categories
      ? defaultFilters.value?.categories
      : filters.value.categories || []

    // categories is the only filter attributes that uses the current filter value in itself,
    // so we need to reset it if resetFilters is true
    if (resetFiltersQuery.value) {
      filters.value = { ...cloneDeep(defaultFilters.value) }
      // resetFilters means "reset every OTHER filter" — a new query is
      // sent alongside it (FederatedSearch's onSubmit), not cleared by it.
      // Without this, the new search term never applies: it's not part of
      // defaultFilters, and this branch returns before the normal
      // `filters.value.query = ...` line below ever runs.
      filters.value.query = queryParam.value?.toString()

      delete filters.value.resetFilters
      router.replace({
        query: {
          ...route.query,
          resetFilters: undefined,
        },
      })
      return
    }

    filters.value.categories = uniq([
      ...filters.value.categories,
      ...(Array.isArray(categoriesQuery.value)
        ? categoriesQuery.value
        : categoriesQuery.value
          ? [categoriesQuery.value]
          : []
      ).map((c) => c?.toString() || ''),
    ])

    filters.value.query = queryParam.value?.toString()

    if (datesQuery.value && datesQuery.value.length > 0) {
      filters.value.dates = [
        datesQuery.value[0],
        datesQuery.value[1] || datesQuery.value[0],
      ]
    }

    filters.value.location = locationSlug.value
    filters.value.locationType = locationTypeQuery.value?.toString()

    if (route.query.priceMax) {
      filters.value.priceMax = +(priceMaxQuery.value ?? 999999)
      filters.value.priceMin = +(priceMinQuery.value ?? 0)
    }

    if (hasAvailableDatesQuery.value) {
      if (hasAvailableDatesQuery.value === 'true') {
        filters.value.hasAvailableDates = true
      } else if (hasAvailableDatesQuery.value === 'false') {
        filters.value.hasAvailableDates = false
      }
    }

    if (hasPromotion.value) {
      if (hasPromotion.value === 'true') {
        filters.value.hasPromotion = true
      }
    }

    if (typeOfClass.value) {
      filters.value.typeOfClass = typeOfClass.value
    }

    if (languagesQuery.value) {
      if (Array.isArray(languagesQuery.value)) {
        filters.value.languages = languagesQuery.value as string[]
      } else {
        filters.value.languages = [languagesQuery.value]
      }
    }
  }

  watchEffect(() => updateFilters())

  return { filters }
}
